import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { useOrganization } from "~/hooks/useOrganization";
import ArrowDropDown from "~/images/arrow-drop-down.svg?react";
import CreditIcon from "~/images/credits.svg?react";
import type { RootState } from "~/store";
import { startTutorialTour } from "~/utils/tutorialEvents";
import CreditSelector from "./CreditInfo";
import styles from "./index.module.scss";
import { UserDropdown } from "./UserDropdown";

const Header: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotifications, setIsNotifications] = useState(false);
  const [isCreditInfo, setIsCreditInfo] = useState(false);
  const [rssData, setRssData] = useState<any[]>([]);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const navigate = useNavigate();
  const notificationsRef = useRef<HTMLDivElement>(null);

  const { currentUser, organization } = useSelector((state: RootState) => state.auth);

  const { isLoading, error } = useOrganization(currentUser?.organization_id);

  const RSS_LAST_VIEWED_KEY = "reqxign-rss-last-viewed";

  const getLastViewedTimestamp = useCallback((): number => {
    const stored = localStorage.getItem(RSS_LAST_VIEWED_KEY);
    return stored ? parseInt(stored, 10) : 0;
  }, []);

  const updateLastViewedTimestamp = useCallback((): void => {
    const now = Date.now();
    localStorage.setItem(RSS_LAST_VIEWED_KEY, now.toString());
  }, []);

  const checkForUnreadNotifications = useCallback(
    (notifications: any[]): boolean => {
      if (!notifications || notifications.length === 0) return false;

      const lastViewed = getLastViewedTimestamp();
      return notifications.some((notification) => {
        const pubDate = new Date(notification.pubDate).getTime();
        return pubDate > lastViewed;
      });
    },
    [getLastViewedTimestamp],
  );

  // Show error toast if organization fetch fails
  React.useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Fetch RSS data from the URL using fetch API
  useEffect(() => {
    const fetchRssData = async () => {
      setIsLoadingNotifications(true);
      try {
        const response = await fetch("https://www.reqxign.net/rss/WaaIb18gJHocU44zSzni");
        const text = await response.text();

        // Parse the XML data using DOMParser
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");

        // Extract the items (notifications) from the XML
        const items = xmlDoc.getElementsByTagName("item");
        const notifications = Array.from(items).map((item: any) => ({
          title: item.getElementsByTagName("title")[0].textContent,
          link: item.getElementsByTagName("link")[0].textContent,
          pubDate: item.getElementsByTagName("pubDate")[0].textContent,
        }));

        setRssData(notifications);

        const hasUnread = checkForUnreadNotifications(notifications);
        setHasUnreadNotifications(hasUnread);
      } catch (error) {
        console.error("Error fetching RSS data:", error);
        toast.error(TOAST_MESSAGES.LAYOUT.FETCH_NOTIFICATIONS_FAILED);
        setHasUnreadNotifications(false);
      } finally {
        setIsLoadingNotifications(false);
      }
    };

    fetchRssData();
  }, [checkForUnreadNotifications]);

  // Handle click outside for notifications modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotifications(false);
      }
    };

    if (isNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNotifications]);

  const handleLogoClick = () => {
    navigate(`/${currentUser.organization_id}/home`);
  };

  const toggleNotifications = () => {
    setIsNotifications((prev) => {
      const newState = !prev;
      if (newState) {
        updateLastViewedTimestamp();
        setHasUnreadNotifications(false);
      }
      return newState;
    });
  };

  const toggleCreditInfo = () => {
    setIsCreditInfo((prev) => !prev);
  };

  return (
    <div className={styles["header"]}>
      <div className={styles["header__logo"]}>
        <div
          className={styles["icon-logo"]}
          onClick={() => navigate(`/${currentUser.organization_id}/home`)}
        />
        <p className={styles["project-name"]} onClick={handleLogoClick}>
          プロジェクト
        </p>
      </div>

      <nav className={styles["nav"]}>
        <div className={styles["notifications"]}>
          <div className={styles["credit-wrapper"]}>
            <div className={styles["credit-icon-wrapper"]} onClick={toggleCreditInfo}>
              <CreditIcon />
            </div>
            <div className={styles["credit-selector"]}>
              <CreditSelector />
            </div>
          </div>
          {isCreditInfo && (
            <div className={styles["creditInfo-wrap"]}>
              <div className={styles["creditInfo-title"]}>
                <CreditSelector variant="compact" />
              </div>
            </div>
          )}

          <div style={{ position: "relative" }} ref={notificationsRef}>
            <div className={styles["notifications-icon-wrapper"]} onClick={toggleNotifications}>
              <i className={styles["icon-notifications"]} />
              {hasUnreadNotifications && <div className={styles["unread-indicator"]} />}
            </div>
            {isNotifications && (
              <div className={styles["notifications-wrap"]}>
                <p className={styles["notifications-title"]}>お知らせ</p>
                {isLoadingNotifications ? (
                  <ul className={styles["notifications-list"]}>
                    {[1, 2, 3].map((_, index) => (
                      <li key={index} className={styles["notifications-listItem"]}>
                        <div className={styles["notifications-skeleton"]}>
                          <div className={styles["notifications-skeleton-title"]}></div>
                          <div className={styles["notifications-skeleton-date"]}></div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className={styles["notifications-list"]}>
                    {rssData.slice(0, 3).map((notification, index) => (
                      <li key={index} className={styles["notifications-listItem"]}>
                        <a href={notification.link} target="_blank" rel="noreferrer">
                          <div className={styles["notifications-listItem-content"]}>
                            <p className={styles["notifications-listItem-content-title"]}>
                              {notification.title}
                            </p>
                          </div>
                          <div className={styles["notifications-listItem-status"]}>
                            <p className={styles["notifications-listItem-status-date"]}>
                              {new Date(notification.pubDate).toLocaleString()}
                            </p>
                          </div>
                        </a>
                      </li>
                    ))}
                    <li className={styles["notifications-listItem"]}>
                      <a href="https://www.reqxign.net/posts" target="_blank" rel="noreferrer">
                        <div className={styles["notifications-listItem-content"]}>
                          <p className={styles["notifications-listItem-content-title"]}>
                            すべて見る
                          </p>
                        </div>
                      </a>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <div
          className={styles["header__icons"]}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          data-testid="user-menu-trigger"
        >
          <i className={styles["icon-user"]} />
          <div className={styles["displayNameOrg"]}>
            <div className={styles["displayNameOrg_container"]}>
              <div className={styles["displayNameOrg_userName"]} data-testid="user-name-display">
                {currentUser?.display_name}
              </div>
              <div className={styles["displayNameOrg_orgName"]}>
                {isLoading ? "読み込み中..." : organization.name}
              </div>
            </div>
            <span className={styles["arrowDropDown"]}>
              <ArrowDropDown />
            </span>
          </div>

          <UserDropdown
            isOpen={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            onStartTutorial={startTutorialTour}
          />
        </div>
      </nav>
    </div>
  );
};

export default Header;
